import { useCallback, useEffect, useRef, useState } from 'react'
import { Subscription } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import {
  SpjsStandCentral,
  getSharedSpjsStandCentral,
} from '@/ble/infrastructures/spjsStandCentral'
import { StandOperationRepository } from '@/ble/repositories/standOperationRepository'
import {
  StandService,
  StandOperationOutcomeResult,
  PostBleState,
} from '@/ble/usecases/standService'
import { BleConnectionPhase } from '@/ble/entities/bleConnectionPhase'
import {
  StandOperationState,
  IDLE_OPERATION_STATE,
} from '@/ble/entities/standOperationState'
import { useRequest } from '@/utils/request'

export type StandOperationExecutionResult =
  | { kind: 'completed'; result: StandOperationOutcomeResult }
  | { kind: 'manualCompleted' }
  | { kind: 'cancelled' }

export type UseStandOperation = {
  execute(params: {
    action: 'rental' | 'return'
    standId: string
    ikasaId: string
    processId: string
  }): Promise<StandOperationExecutionResult>
  phase: BleConnectionPhase
  operationState: StandOperationState
  manualComplete(): void
  cancel(): void
}

// テスト用 DI（本番は省略で SpjsStandCentral + useRequest(silent) を使う）。
export function useStandOperation(deps?: {
  central?: SpjsStandCentral
  postBleState?: PostBleState
}): UseStandOperation {
  const request = useRequest()
  const centralRef = useRef<SpjsStandCentral | null>(null)
  if (centralRef.current === null) {
    // 本番はモジュール共有インスタンス（adapter 直列化キューを画面 mount 跨ぎで1本に保つ・#7706 G3）。
    centralRef.current = deps?.central ?? getSharedSpjsStandCentral()
  }
  const abortRef = useRef<AbortController | null>(null)
  const repoRef = useRef<StandOperationRepository | null>(null)
  const subRef = useRef<Subscription | null>(null)
  const opSubRef = useRef<Subscription | null>(null)
  const serviceRef = useRef<StandService | null>(null)
  const [phase, setPhase] = useState<BleConnectionPhase>({ type: 'idle' })
  const [operationState, setOperationState] =
    useState<StandOperationState>(IDLE_OPERATION_STATE)

  // /ble/state POST は silent（toast/loading なし）で送る。payload は素通し（backend adapter が
  // when/state/app_version を無条件 deref するため加工しない）。
  // useCallback で識別を安定させる（request は useRequest の安定 useCallback）。これにより DI 無し
  // (本番)経路でも execute の識別が render 跨ぎで安定し、consumer の useEffect([execute]) が
  // 毎 render 再実行されない（UnlockPage の起動 effect が deps に execute を含む前提）。
  const defaultPostBleState = useCallback<PostBleState>(
    async (payload) => {
      await request('/service/v2/ble/state', {
        method: 'POST',
        data: payload as any,
        showToast: false,
        showLoading: false,
      })
    },
    [request]
  )
  const postBleState = deps?.postBleState ?? defaultPostBleState

  // タップは実行中操作の usecase へ委譲するだけ。claim(先勝ち)の調停は React の外(service)が持つため、
  // execute 完了後(serviceRef=null)や既に claim 済みの service への呼び出しは自然に no-op になる。
  const manualComplete = useCallback(() => {
    serviceRef.current?.manualComplete()
  }, [])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    // signal は connect のスキャン待ちのみ被覆。操作中(write/result待ち)は disconnect で畳む
    //（idempotent）。disconnect→operationResult$ complete→service reject を execute catch が拾う。
    void repoRef.current?.disconnect().catch(() => undefined)
  }, [])

  // execute は直列使用が契約（同一 hook インスタンスで同時に2回 execute しない）。
  // abortRef/repoRef が単一スロットなのはこの契約の下でのみ妥当。phase 購読だけは
  // execute ローカルの変数で持ち、finally で「自分の sub」だけを解除する（並行呼び出しがあっても
  // 他方の購読を誤って握り潰さない）。
  // execute の settle 時点では後始末（disconnect）が裏で進行中のことがあり、phase の 'idle' 収束は
  // 結果より数秒（後始末の cap 上限まで）遅れ得る。UnlockPage は結果で画面遷移し phase の idle を
  // 待たないため影響しない（#7706）。
  const execute = useCallback(
    async (params: {
      action: 'rental' | 'return'
      standId: string
      ikasaId: string
      processId: string
    }): Promise<StandOperationExecutionResult> => {
      const central = centralRef.current!
      const abort = new AbortController()
      abortRef.current = abort
      const repo = new StandOperationRepository({ central })
      repoRef.current = repo // cancel() が操作中に disconnect できるよう保持
      const sub = repo.observeConnectionPhase$
        .pipe(distinctUntilChanged((a, b) => a.type === b.type)) // UnlockPage は type しか見ないので type 単位で dedup
        .subscribe(setPhase)
      subRef.current = sub // cancel()/unmount からも最新の購読を解放できるよう保持
      const service = new StandService(repo, postBleState)
      serviceRef.current = service // manualComplete() が実行中の service に委譲できるよう保持
      const opSub = service.observeOperationState$
        .pipe(
          distinctUntilChanged(
            (a, b) =>
              a.stage === b.stage &&
              a.offerManualCompletion === b.offerManualCompletion
          )
        )
        .subscribe(setOperationState)
      opSubRef.current = opSub
      try {
        const peripheral = await repo.connect(params.standId, {
          signal: abort.signal,
        })
        if (peripheral === null) return { kind: 'cancelled' } // connect 中の cancel
        const ctx = {
          ikasaId: params.ikasaId,
          standId: params.standId,
          processId: params.processId,
          appVersion: 'SPOT mini App',
        }
        const outcome =
          params.action === 'rental'
            ? await service.takeOut(ctx)
            : await service.putIn(ctx)
        if (outcome.kind === 'manualCompleted') {
          return { kind: 'manualCompleted' } // manualComplete() の claim 勝ちが runFlow の Promise.race を解決した経路
        }
        return { kind: 'completed', result: outcome.result }
      } catch (e) {
        // 操作中(write/result待ち)の cancel/unmount は disconnect→stream complete→service reject
        // になる。signal.aborted なら中断として畳む（画面にエラーを出さない）。それ以外は timeout/error。
        if (abort.signal.aborted) return { kind: 'cancelled' }
        throw e
      } finally {
        sub.unsubscribe()
        if (subRef.current === sub) subRef.current = null // 自分の sub がまだ current の時だけ null 化
        opSub.unsubscribe()
        if (opSubRef.current === opSub) opSubRef.current = null
        if (serviceRef.current === service) serviceRef.current = null // 完了後の manualComplete() を no-op 化
      }
    },
    [postBleState]
  )

  // unmount: in-flight を cancel（cancel() と同一シーケンス）＋購読解放。
  // execute の finally は disconnect 完了後（非同期）に走るため、unmount 時点ではまだ
  // phase/operationState の購読が残っている可能性がある。ここで即座に解放する。
  useEffect(() => {
    return () => {
      cancel()
      subRef.current?.unsubscribe()
      subRef.current = null
      opSubRef.current?.unsubscribe()
      opSubRef.current = null
    }
  }, [cancel])

  return { execute, phase, operationState, manualComplete, cancel }
}
